import {
  DataType,
  type SettingTypeMetadata,
  type SettingTypeValueMap,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createSettingsParams,
  type ISettingsParam,
  type PartialBy,
  SettingsParamType,
} from '@wix/tpa-settings';
import {
  AlignmentOptions,
  LayoutTypes,
  SlotsAvailability,
} from '../../types/types';
import { AccessibilityHtmlTags } from '../../utils/a11y/consts';

export const withoutDefaultFallback = (settingsParam: ISettingsParam) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});
export type ISettingsParams = {
  timeSlotsDisplay: SlotsAvailability;
  headerSticky: SettingsParamType.Boolean;
  limitTimeSlots: SettingsParamType.Boolean;
  showLoadMoreButton: SettingsParamType.Boolean;
  maxTimeSlotsPerDay: SettingsParamType.Number;
  scrollBetweenDays: SettingsParamType.Boolean;
  selectedLocations: SettingsParamType.Text[];
  selectedCustomLocation: SettingsParamType.Boolean;
  selectedCategories: SettingsParamType.Text[];
  titleText: SettingsParamType.Text;
  titleTagName: SettingsParamType.String;
  dateTagName: SettingsParamType.String;
  headerFiltersTagName: SettingsParamType.String;
  tableDatesTagName: SettingsParamType.String;
  sessionsItemsServiceNameTag: SettingsParamType.String;
  sessionItemsRegularButtonNameTag: SettingsParamType.String;
  sessionsItemsPrimaryNameTag: SettingsParamType.String;
  loadMoreButtonNameTag: SettingsParamType.String;
  sessionsItemsSecondaryNameTag: SettingsParamType.String;
  tableDayTagName: SettingsParamType.String;
  selectedServices: SettingsParamType.String[];
  timeSlotsPerDay: SettingsParamType.Number;
  spotsLeftVisibility: SettingsParamType.Boolean;
  noSpotsLeft: SettingsParamType.Text;
  serviceLabel: SettingsParamType.Text;
  staffMemberLabel: SettingsParamType.Text;
  locationLabel: SettingsParamType.Text;
  timeSlotsBeforeLoadMore: SettingsParamType.Number;
  toEarlyToBookIndication: SettingsParamType.Text;
  toLateToBookIndication: SettingsParamType.Text;
  bookOnlineDisabledIndication: SettingsParamType.Text;
  joinWaitlistButton: SettingsParamType.Text;
  pendingApprovalButton: SettingsParamType.Text;
  bookButtonText: SettingsParamType.Text;
  loadMoreButton: SettingsParamType.Text;
  alreadyBookedButton: SettingsParamType.Text;
  blockedDayText: SettingsParamType.Text;
  layoutStyle: SettingsParamType.String;
  sessionItemsAlignment: SettingsParamType.String;
  sessionItemsAlignmentOverlay: SettingsParamType.String;
};

export type ISettingsParamsDefinitions = {
  [key in keyof ISettingsParams]: PartialBy<
    ISettingsParam<ISettingsParams[key]>,
    'key'
  >;
};

const getMetadata = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
): SettingTypeMetadata => ({
  type: type as DataType.UNKNOWN_DataType, // type assertion is needed to avoid strange TypeScript error
  defaultValue: {
    type: 'static',
    value: defaultValue,
  },
});

const settingsParam = <T extends DataType>(
  type: T,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(type, defaultValue),
});

const enumSettingsParam = <T extends string>(defaultValue: T) => ({
  getDefaultValue: () => defaultValue,
  metadata: getMetadata(DataType.textEnum, defaultValue),
});

const typedSettingsParam = <T extends DataType, Type extends SettingsParamType>(
  dataType: T,
  type: Type | undefined,
  defaultValue: SettingTypeValueMap[T]['value'],
) => ({
  type,
  ...settingsParam(dataType, defaultValue),
});

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

export const settingsParamsDefinitions: ISettingsParamsDefinitions = {
  sessionItemsAlignment: enumSettingsParam(AlignmentOptions.TOP),
  sessionItemsAlignmentOverlay: enumSettingsParam(AlignmentOptions.BOTTOM),
  timeSlotsDisplay: settingsParam(DataType.number, SlotsAvailability.ALL),
  showLoadMoreButton: settingsParam(DataType.booleanValue, true),
  headerSticky: settingsParam(DataType.booleanValue, true),
  limitTimeSlots: settingsParam(DataType.booleanValue, true),
  maxTimeSlotsPerDay: settingsParam(DataType.number, 6),
  scrollBetweenDays: settingsParam(DataType.booleanValue, true),
  selectedLocations: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedCustomLocation: settingsParam(DataType.booleanValue, false),
  selectedCategories: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  selectedServices: typedSettingsParam(
    DataType.container,
    SettingsParamType.Object,
    [],
  ),
  timeSlotsPerDay: settingsParam(DataType.number, 10),
  titleText: textSettingsParam('daily-agenda.settings.text.title.placeholder'),
  titleTagName: enumSettingsParam(AccessibilityHtmlTags.SecondaryHeading),
  dateTagName: enumSettingsParam(AccessibilityHtmlTags.SecondaryHeading),
  headerFiltersTagName: enumSettingsParam(
    AccessibilityHtmlTags.SecondaryHeading,
  ),
  tableDatesTagName: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  tableDayTagName: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  sessionItemsRegularButtonNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  loadMoreButtonNameTag: enumSettingsParam(AccessibilityHtmlTags.Paragraph),
  spotsLeftVisibility: settingsParam(DataType.booleanValue, true),
  sessionsItemsServiceNameTag: enumSettingsParam(
    AccessibilityHtmlTags.MainHeading,
  ),
  sessionsItemsPrimaryNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  sessionsItemsSecondaryNameTag: enumSettingsParam(
    AccessibilityHtmlTags.Paragraph,
  ),
  noSpotsLeft: textSettingsParam(
    'daily-agenda.settings.text.button.no-availability-text.placeholder',
  ),
  serviceLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.service-label.placeholder',
  ),
  staffMemberLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.staff-member-label.placeholder',
  ),
  locationLabel: textSettingsParam(
    'daily-agenda.settings.text.filters-and-preferences.location-label.placeholder',
  ),
  layoutStyle: enumSettingsParam(LayoutTypes.LIST),
  timeSlotsBeforeLoadMore: settingsParam(DataType.number, 12),
  toEarlyToBookIndication: textSettingsParam(
    'daily-agenda.settings.text.button.to-early-to-book.placeholder',
  ),
  toLateToBookIndication: textSettingsParam(
    'daily-agenda.settings.text.button.to-late-to-book.placeholder',
  ),
  bookOnlineDisabledIndication: textSettingsParam(
    'app.settings.defaults.button.more-info',
  ),
  joinWaitlistButton: textSettingsParam(
    'daily-agenda.settings.text.button.wait-list.placeholder',
  ),
  pendingApprovalButton: textSettingsParam(
    'app.settings.defaults.booking-details.pending-approval.text',
  ),
  bookButtonText: textSettingsParam(
    'daily-agenda.settings.text.button.book-button.placeholder',
  ),
  loadMoreButton: textSettingsParam(
    'daily-agenda.settings.text.button.load-more-tome-slots.placeholder',
  ),
  alreadyBookedButton: textSettingsParam(
    'daily-agenda.settings.text.button.already-booked.placeholder',
  ),
  blockedDayText: textSettingsParam(
    'daily-agenda.settings.text.table-slots.blocked-day.placeholder',
  ),
};

export default createSettingsParams<ISettingsParams>(settingsParamsDefinitions);
